import React, { useContext, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import mainStyles from "styles";

import { PlanType } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/admin/v1/subscriptions_pb";
import { Tier } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/common_pb";
import { ComponentType } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/components_pb";
import { SubscriptionState } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/subscriptions_pb";
import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";

import CancelSubscription from "./subscription-actions/cancel-subscription";
import ChangeBillingFrequency from "./subscription-actions/change-billing-frequency";
import UpdateCancelAtEndOfPeriod from "./subscription-actions/change-cancel-at-end-of-period";
import ChangeComponentPricePoint from "./subscription-actions/change-component-price-point";
import ChangeContractTerm from "./subscription-actions/change-contract-term";
import ChangeProductType from "./subscription-actions/change-product-type";
import ChangeSubscriptionCurrency from "./subscription-actions/change-subscription-currency";
import ChangeTrialDateZuora from "./subscription-actions/change-trial-date-zuora";
import DowngradeToFreePlan from "./subscription-actions/downgrade-to-free-plan";
import ManageCoupons from "./subscription-actions/manage-coupons";
import PauseSync from "./subscription-actions/pause-sync";
import ReactivateSubscriptionZuora from "./subscription-actions/reactivate-subscription-zuora";
import ReconcileSubscription from "./subscription-actions/reconcile-subscription";
import RemovePaymentMethod from "./subscription-actions/remove-payment-method";
import SetCustomField from "./subscription-actions/set-custom-field";
import SetSeatLimitRestriction from "./subscription-actions/set-seat-limit-restriction";
import UpdatePaymentCollectionMethodZuora from "./subscription-actions/update-payment-collection-method-zuora";
import UpdateSeatMovementsRestriction from "./subscription-actions/update-seat-movements-restriction";
import UpdateTaxExemptionZuora from "./subscription-actions/update-tax-exemption-zuora";
import UpgradeWithoutCreditCard from "./subscription-actions/upgrade-without-credit-card";
import DisplayHeading from "components/shared/display-heading";
import { FEATURE_FLAG_KEY_DISABLE_BILLING_UPDATES } from "constants/billing-experiences";
import { UseGetFeatureFlagValue } from "context/feature-flags-context";
import { OrgDataContext, SubDataContext } from "context/organisation-context";
import { PermissionsContext } from "context/permissions-context";
import { isUnderZuoraMigration } from "helpers/subscriptions";

const ZUORA_DISABLED_MESSAGE =
  "This action is temporarily unavailable while we transition to the Zuora Billing Engine. It will be back soon — thanks for your patience!";

const BILLING_UPDATES_DISABLED_MESSAGE =
  "Billing updates are disabled for this organisation, possibly because the subscription has a rate plan that is not fully supported.";

const DisabledAction: React.FC<{
  title?: string;
  testId?: string;
  children: React.ReactNode;
}> = ({
  title = ZUORA_DISABLED_MESSAGE,
  testId = "zuora-transition-disabled-action",
  children,
}) => (
  <Tooltip title={title} followCursor>
    <Box sx={{ opacity: 0.5 }} data-testid={testId}>
      <Box sx={{ pointerEvents: "none" }}>{children}</Box>
    </Box>
  </Tooltip>
);

interface SubscriptionsTabProps {
  refreshOrgData: () => void;
}

const SubscriptionsTabZuora: React.FC<SubscriptionsTabProps> = ({
  refreshOrgData,
}: SubscriptionsTabProps) => {
  const classes = mainStyles();
  const theme = useTheme();
  const { orgData } = useContext(OrgDataContext);
  const { subData } = useContext(SubDataContext);
  const { hasPermissions, hasGroups, permissions } =
    useContext(PermissionsContext);
  const subscription = useMemo(() => {
    return orgData.subscription && orgData.subscription[0];
  }, [orgData]);

  const zuoraSubscription = subData?.subscription;

  const isMigrating = isUnderZuoraMigration(
    zuoraSubscription?.zuoraMigrationStatus,
  );

  const [migrationAcknowledged, setMigrationAcknowledged] = useState(false);

  const [subPlanType, setSubPlanType] = useState(PlanType.UNSPECIFIED);

  const refreshSubPlanType = (newPlanType: PlanType) => {
    setSubPlanType(newPlanType);
  };

  const canUpdateContractTerm =
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.UPDATE_CONTRACT_TERM]);

  const canPauseSalesForceFync =
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.PAUSE_SALESFORCE_SYNC]);

  const canReconcileSubscription =
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.RECONCILE_SUBSCRIPTION]);

  const canUpdateBillingFrequency =
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.UPDATE_BILLING_FREQUENCY]);

  // Billing updates are disabled for this org. The usual cause is a Zuora rate plan
  // charge our product catalog doesn't support: every action that rebuilds rate plans
  // re-resolves each charge against the catalog and silently drops or re-prices the ones
  // it can't find — after the live subscription has already been cancelled. The flag is a
  // general kill switch though (orgs can also be added by hand, or for decoupled
  // billing), so don't assume the cause anywhere user-facing. Block those actions
  // regardless, since none of them are safe while it's on (FG-6190).
  const billingUpdatesDisabled = Boolean(
    UseGetFeatureFlagValue(FEATURE_FLAG_KEY_DISABLE_BILLING_UPDATES),
  );

  const blockWhenBillingUpdatesDisabled = (action: React.ReactNode) =>
    billingUpdatesDisabled ? (
      <DisabledAction
        title={BILLING_UPDATES_DISABLED_MESSAGE}
        testId="billing-updates-disabled-action"
      >
        {action}
      </DisabledAction>
    ) : (
      action
    );

  const isFreeOrg = zuoraSubscription?.tier === Tier.FREE;

  const isOnZuora =
    (zuoraSubscription?.providerSubscriptionId &&
      zuoraSubscription?.providerSubscriptionId?.length > 0) ||
    false;

  // isOnZuora is really "has a live Zuora subscription": providerSubscriptionId is the Zuora
  // subscription number, which a decoupled trial does not have until it converts. This tab is
  // already only rendered for BillingProvider.ZUORA (see pages/organisation.tsx), so the provider
  // is not in question here. The payment collection method is an account-level setting, so it must
  // stay settable while the trial is still decoupled — that is how an org is switched to invoicing
  // before its trial ends.
  const canUpdatePaymentCollectionMethod = !isFreeOrg;

  const shouldDisplayChangeBillingFreq =
    !isFreeOrg &&
    canUpdateBillingFrequency &&
    zuoraSubscription?.state !== undefined &&
    [SubscriptionState.TRIALING, SubscriptionState.ACTIVE].includes(
      zuoraSubscription?.state,
    );

  const shouldDisplayChangeContractTerm =
    !isFreeOrg &&
    canUpdateContractTerm &&
    zuoraSubscription?.state !== undefined &&
    [SubscriptionState.TRIALING, SubscriptionState.ACTIVE].includes(
      zuoraSubscription?.state,
    );

  const trialCancelEndOfPeriodRestrictedGroups =
    !permissions.isPermissionsLoading &&
    hasGroups(["App - Business Tools - AE", "App - Business Tools - BD"]);

  const canCancelAtEndOfPeriodState =
    zuoraSubscription?.state &&
    ![SubscriptionState.CANCELED, SubscriptionState.PAST_DUE].includes(
      zuoraSubscription?.state,
    );

  const renderCancelAtEndOfPeriod = () => {
    if (
      (!isFreeOrg &&
        canCancelAtEndOfPeriodState &&
        trialCancelEndOfPeriodRestrictedGroups &&
        zuoraSubscription?.state === SubscriptionState.TRIALING &&
        isOnZuora) ||
      (!isFreeOrg &&
        canCancelAtEndOfPeriodState &&
        !trialCancelEndOfPeriodRestrictedGroups &&
        isOnZuora)
    ) {
      return (
        <TableRow>
          <TableCell sx={classes.tableCellNew}>
            <UpdateCancelAtEndOfPeriod refreshOrgData={refreshOrgData} />
          </TableCell>
        </TableRow>
      );
    }
  };

  if (isMigrating && !migrationAcknowledged) {
    return (
      <Grid container>
        <Grid item xs={12} sx={classes.paddingTopSmall}>
          <DisplayHeading>Manage Subscription</DisplayHeading>
        </Grid>
        <Grid item xs={12}>
          <Alert severity="warning" sx={{ textAlign: "start", m: 2 }}>
            <Typography
              variant="body1"
              gutterBottom
              sx={{ fontWeight: "bold" }}
            >
              Zuora Billing Migration In Progress
            </Typography>
            <Typography variant="body2" gutterBottom>
              This organisation is currently undergoing a Zuora billing
              migration. Billing changes may be rejected or cause unexpected
              issues during this period.
            </Typography>
            <Typography variant="body2" gutterBottom>
              Please avoid making billing changes unless absolutely necessary.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 2 }}
              onClick={() => setMigrationAcknowledged(true)}
            >
              I Understand, Proceed
            </Button>
          </Alert>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container>
      <Grid item xs={12} sx={classes.paddingTopSmall}>
        <DisplayHeading>Manage Subscription</DisplayHeading>
      </Grid>
      {billingUpdatesDisabled && (
        <Grid item xs={12}>
          <Alert
            severity="warning"
            sx={{ textAlign: "start", m: 2 }}
            data-testid="billing-updates-disabled-warning"
          >
            <Typography
              variant="body1"
              gutterBottom
              sx={{ fontWeight: "bold" }}
            >
              Billing Updates Disabled
            </Typography>
            <Typography variant="body2" gutterBottom>
              Billing updates are disabled for this organisation, possibly
              because the subscription has a rate plan that is not fully
              supported. Changes that rebuild rate plans — product type,
              contract term, billing frequency, currency, ending the trial,
              reactivating, and coupons — have been disabled.
            </Typography>
          </Alert>
        </Grid>
      )}
      <Grid item xs={12}>
        <Box
          style={{
            borderColor: theme.palette.divider,
            borderWidth: "1px",
            borderStyle: "solid",
            borderRadius: "5px",
            margin: "20px",
          }}
        >
          <Table size="medium">
            <TableBody>
              {canUpdatePaymentCollectionMethod && (
                <TableRow>
                  <TableCell sx={classes.tableCellNew}>
                    <Grid>
                      <UpdatePaymentCollectionMethodZuora
                        refreshOrgData={refreshOrgData}
                      />
                    </Grid>
                  </TableCell>
                </TableRow>
              )}

              <TableRow>
                <TableCell sx={classes.tableCellNew}>
                  <RemovePaymentMethod refreshOrgData={refreshOrgData} />
                </TableCell>
              </TableRow>

              {canPauseSalesForceFync && (
                <TableRow>
                  <TableCell sx={classes.tableCellNew}>
                    <DisabledAction>
                      <PauseSync />
                    </DisabledAction>
                  </TableCell>
                </TableRow>
              )}

              {canReconcileSubscription && (
                <TableRow>
                  <TableCell sx={classes.tableCellNew}>
                    <ReconcileSubscription refreshOrgData={refreshOrgData} />
                  </TableCell>
                </TableRow>
              )}

              {isOnZuora && (
                <TableRow>
                  <TableCell sx={classes.tableCellNew}>
                    <UpdateTaxExemptionZuora />
                  </TableCell>
                </TableRow>
              )}

              {!isFreeOrg && (
                <TableRow>
                  <TableCell sx={classes.tableCellNew}>
                    {blockWhenBillingUpdatesDisabled(<ChangeProductType />)}
                  </TableCell>
                </TableRow>
              )}

              {shouldDisplayChangeContractTerm && isOnZuora && (
                <>
                  <TableRow>
                    <TableCell sx={classes.tableCellNew}>
                      {blockWhenBillingUpdatesDisabled(
                        <ChangeContractTerm
                          refreshOrgData={refreshOrgData}
                          isZuora={true}
                        />,
                      )}
                    </TableCell>
                  </TableRow>
                </>
              )}

              {shouldDisplayChangeBillingFreq && isOnZuora && (
                <>
                  <TableRow>
                    <TableCell sx={classes.tableCellNew}>
                      {blockWhenBillingUpdatesDisabled(
                        <ChangeBillingFrequency />,
                      )}
                    </TableCell>
                  </TableRow>
                </>
              )}

              {/* Only display cancel at end of period if subscription is not past_due or canceled*/}
              {renderCancelAtEndOfPeriod()}

              <TableRow>
                <TableCell sx={classes.tableCellNew}>
                  <CancelSubscription refreshOrgData={refreshOrgData} />
                </TableCell>
              </TableRow>

              {/* ONLY RENDER TRIAL DATE COMPONENT WHEN SUBSCRIPTION IS TRIALING */}
              {zuoraSubscription?.state === SubscriptionState.TRIALING && (
                <TableRow>
                  <TableCell sx={classes.tableCellNew}>
                    {blockWhenBillingUpdatesDisabled(
                      <ChangeTrialDateZuora
                        subscription={zuoraSubscription}
                        refreshOrgData={refreshOrgData}
                      />,
                    )}
                  </TableCell>
                </TableRow>
              )}

              {/* Only display upgrade if subscription is a free organisation*/}
              {isFreeOrg && (
                <TableRow>
                  <TableCell sx={classes.tableCellNew}>
                    <UpgradeWithoutCreditCard refreshOrgData={refreshOrgData} />
                  </TableCell>
                </TableRow>
              )}

              {/* Only display reactivate option if subscription is canceled*/}
              {zuoraSubscription?.state === SubscriptionState.CANCELED && (
                <TableRow>
                  <TableCell sx={classes.tableCellNew}>
                    {blockWhenBillingUpdatesDisabled(
                      <ReactivateSubscriptionZuora
                        refreshOrgData={refreshOrgData}
                        subscription={zuoraSubscription}
                      />,
                    )}
                  </TableCell>
                </TableRow>
              )}

              {/* Only display downgrade option if subscriptions is not a free organisation */}
              {/* Deliberately NOT blocked when billing updates are disabled (FG-6190):
                  downgrading is how support rescues an org whose subscription is already
                  unrecoverable, so it must stay available. */}
              {!isFreeOrg && (
                <TableRow>
                  <TableCell sx={classes.tableCellNew}>
                    <DowngradeToFreePlan refreshOrgData={refreshOrgData} />
                  </TableCell>
                </TableRow>
              )}

              {isOnZuora && (
                <TableRow>
                  <TableCell sx={classes.tableCellNew}>
                    <UpdateSeatMovementsRestriction
                      subscription={subscription}
                    />
                  </TableCell>
                </TableRow>
              )}

              {!isFreeOrg && (
                <TableRow>
                  <TableCell sx={classes.tableCellNew}>
                    <SetSeatLimitRestriction subscription={subscription} />
                  </TableCell>
                </TableRow>
              )}

              {!isFreeOrg && (
                <TableRow>
                  <TableCell sx={classes.tableCellNew}>
                    {blockWhenBillingUpdatesDisabled(<ManageCoupons />)}
                  </TableCell>
                </TableRow>
              )}

              <TableRow>
                <TableCell sx={classes.tableCellNew}>
                  {blockWhenBillingUpdatesDisabled(
                    <ChangeSubscriptionCurrency
                      refreshOrgData={refreshOrgData}
                      provider="zuora"
                    />,
                  )}
                </TableCell>
              </TableRow>

              {/* Only show Subscription type and price if they have plans */}
              {!isFreeOrg && isOnZuora && (
                <TableRow>
                  <TableCell sx={classes.tableCellNew}>
                    <DisabledAction>
                      <ChangeComponentPricePoint
                        subscription={subscription}
                        componentType={ComponentType.FULL_SEAT}
                        onPlanTypeUpdate={refreshSubPlanType}
                      />
                    </DisabledAction>
                  </TableCell>
                </TableRow>
              )}

              {!isFreeOrg && isOnZuora && (
                <TableRow>
                  <TableCell sx={classes.tableCellNew}>
                    <DisabledAction>
                      <ChangeComponentPricePoint
                        subscription={subscription}
                        componentType={ComponentType.LITE_SEAT}
                        onPlanTypeUpdate={refreshSubPlanType}
                      />
                    </DisabledAction>
                  </TableCell>
                </TableRow>
              )}

              {subscription.components && isOnZuora && (
                <TableRow>
                  <TableCell sx={classes.tableCellNew}>
                    <DisabledAction>
                      <SetCustomField
                        subscription={subscription}
                        planType={subPlanType}
                      />
                    </DisabledAction>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Grid>
    </Grid>
  );
};

export default SubscriptionsTabZuora;
